import { useMemo } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import type { ChartDataPoint } from '@gonfalon/dataviz-experimental/types/ChartDataPoint';
import { transformChartDataResponse } from '@gonfalon/dataviz-experimental/utils/chartDataTransformer';
import {
  cutoffDateForIterationStartForCHEX,
  utilizeDataVizForExperimentExposuresChart,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { hasWarehouseAssignment } from '@gonfalon/experiments/hasWarehouseAssignment';
import { type Experiment, ExperimentStatusKind, type Iteration } from '@gonfalon/experiments/types';
import { getVariationFillColor } from '@gonfalon/flag-variations/getVariationFillColor';
import { getExperimentTrafficQuery } from '@gonfalon/rest-api/queries/experiments';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useQuery } from '@tanstack/react-query';
import nullthrows from 'nullthrows';

import { getTransformOptions } from '#components/AiMonitoring/util/transformers';
import { usePrimaryMetricAnalysisTime } from '#components/experimentation/common/providers/IterationAnalysisProvider/IterationAnalysisProvider';

import { calculateTrafficTotal } from '../utils/calculateTrafficTotal';
import {
  backfillCumulativeSeriesKeys,
  DATE_AFTER_WHICH_TO_USE_DATAVIZ_DATA,
  getCustomTrafficChartInterval,
  injectChartDataWithTreatmentNames,
  isChartDataResponse,
  TRAFFIC_TARGET_FIELD,
} from '../utils/datavizChartHelpers';

import { useExperimentTrafficData } from './useExperimentTrafficData';

type TrafficTreatmentRow = {
  timestamp?: number | null;
  treatmentTraffic?: { traffic?: number | null } | null;
};

type ExperimentTrafficForChart = {
  treatmentTraffic?: Array<{
    treatmentId?: string | null;
    data?: TrafficTreatmentRow[] | null;
  }> | null;
} | null;

type IterationTreatmentForTrafficChart = {
  _id?: string | null;
  name?: string | null;
};

/**
 * Transforms old API traffic data into ChartDataPoint[] format.
 * Merges treatment timeseries into flat array with treatment names as keys.
 */
function experimentTrafficToLineChartData(
  treatments: IterationTreatmentForTrafficChart[] | undefined,
  experimentTraffic: ExperimentTrafficForChart,
): ChartDataPoint[] {
  if (!treatments?.length) {
    return [];
  }

  const byTimestamp = new Map<number, ChartDataPoint>();
  const allSeriesKeys: string[] = [];

  for (const treatment of treatments) {
    const seriesKey = treatment.name ?? treatment._id ?? '';
    if (!seriesKey) {
      continue;
    }
    allSeriesKeys.push(seriesKey);

    const trafficTreatment = experimentTraffic?.treatmentTraffic?.find((t) => t.treatmentId === treatment._id);
    const rows = trafficTreatment?.data;
    if (!rows?.length) {
      continue;
    }

    const sorted = [...rows].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    for (const row of sorted) {
      const ts = row.timestamp || 0;
      const value = row.treatmentTraffic?.traffic ?? 0;
      const point = byTimestamp.get(ts) ?? { timestamp: ts };
      point[seriesKey] = value;
      byTimestamp.set(ts, point);
    }
  }

  const sorted = Array.from(byTimestamp.entries())
    .sort(([a], [b]) => a - b)
    .map(([, point]) => point);

  return backfillCumulativeSeriesKeys(sorted, allSeriesKeys);
}

export type ExperimentTrafficChartIterationInput = Pick<
  Iteration,
  'startedAt' | 'endedAt' | 'status' | 'treatments' | 'primarySingleMetric' | 'primaryFunnel' | 'assignmentDataSource'
>;

export type ExperimentTrafficChartExperimentInput = Pick<Experiment, 'key' | 'assignmentSourceType'>;

/**
 * Shared hook for experiment traffic chart data.
 * Encapsulates data source selection, fetching, and transformation logic.
 *
 * Uses dataviz API when:
 * - Feature flag `utilizeDataVizForExperimentExposuresChart()` is ON
 * - AND iteration started after Jan 22, 2026
 * - AND the experiment does not assign from a warehouse source, whose exposures dataviz does not serve
 *
 * Skips all traffic requests while `iteration.status` is `not_started`.
 *
 * Otherwise falls back to legacy traffic timeseries via `useQuery(getExperimentTrafficQuery(...))`.
 *
 * @returns chartData - Transformed data ready for chart rendering
 * @returns isLoading - Loading state
 * @returns keyToColorMap - Treatment name/ID to color mapping
 * @returns totalCount - Sum of latest cumulative traffic across series (see calculateTrafficTotal)
 */
export function useExperimentTrafficChartData({
  experiment,
  iterationId,
  iteration,
  flagVariations,
}: {
  experiment: ExperimentTrafficChartExperimentInput;
  iterationId: string;
  iteration: ExperimentTrafficChartIterationInput;
  flagVariations?: Array<{ _id?: string }>;
}) {
  const useDataVizForChart = utilizeDataVizForExperimentExposuresChart();
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { analysisTime, isPending: isAnalysisPending } = usePrimaryMetricAnalysisTime(iteration);

  const trafficFetchEnabled = iteration.status !== ExperimentStatusKind.NOT_STARTED;

  // Check if iteration is new enough for dataviz
  const iterationIsNewEnoughForDatavizData = useMemo(() => {
    const cutoff = Math.max(cutoffDateForIterationStartForCHEX(), DATE_AFTER_WHICH_TO_USE_DATAVIZ_DATA.getTime());
    return new Date(iteration.startedAt ?? 0) > new Date(cutoff);
  }, [iteration.startedAt]);

  const shouldUseDataviz =
    trafficFetchEnabled &&
    useDataVizForChart &&
    iterationIsNewEnoughForDatavizData &&
    !hasWarehouseAssignment(experiment, iteration);
  // Wait only while analysis is still loading so we can use analysisTime as `to` when
  // present. Once analysis settles without analysisTime (pending results / error), fall
  // through to endedAt/now so the chart does not load forever.
  const isWaitingForAnalysis = shouldUseDataviz && isAnalysisPending;
  const datavizFetchEnabled = shouldUseDataviz && !isAnalysisPending;

  // Prefer analysisTime so the exposures chart matches results; placeholder endedAt/now
  // is only used while disabled or when analysis settled without a snapshot time.
  const trafficTimeRange = useMemo(() => {
    const endMs = analysisTime ?? (iteration.endedAt ? new Date(iteration.endedAt).getTime() : Date.now());
    const from = iteration.startedAt ? new Date(iteration.startedAt).getTime() : 0;
    const interval = getCustomTrafficChartInterval(from, endMs);
    return { from, to: endMs, interval };
  }, [iteration.startedAt, iteration.endedAt, analysisTime]);

  const trafficQueryKeys = useMemo(
    () => iteration.treatments?.map((t) => t._id ?? '').filter(Boolean) ?? [],
    [iteration.treatments],
  );

  // Fetch from legacy traffic timeseries API (disabled until iteration has started)
  const { data: experimentTraffic, isFetching } = useQuery({
    ...getExperimentTrafficQuery({
      projectKey,
      environmentKey,
      experimentKey: experiment.key,
      iterationId: nullthrows(iterationId, 'missing iteration id'),
    }),
    enabled: trafficFetchEnabled,
  });

  // Fetch from new dataviz API (conditional)
  const {
    data: experimentTrafficDataviz,
    isLoading: isTrafficDatavizLoading,
    isError: isTrafficDatavizError,
  } = useExperimentTrafficData(trafficTimeRange, projectKey, environmentKey, trafficQueryKeys, datavizFetchEnabled);

  // Transform dataviz API response
  const datavizChartData = useMemo(() => {
    if (!useDataVizForChart || !isChartDataResponse(experimentTrafficDataviz)) {
      return [];
    }
    const transformOptions = getTransformOptions(false);
    const experimentTrafficDatavizWithIterationNames = injectChartDataWithTreatmentNames(
      experimentTrafficDataviz,
      iteration.treatments ?? [],
    );
    const transformed = transformChartDataResponse(
      experimentTrafficDatavizWithIterationNames,
      TRAFFIC_TARGET_FIELD,
      transformOptions,
    );
    const treatmentSeriesKeys = (iteration.treatments ?? []).map((t) => t.name ?? t._id ?? '').filter(Boolean);
    return backfillCumulativeSeriesKeys(transformed, treatmentSeriesKeys);
  }, [useDataVizForChart, experimentTrafficDataviz, iteration.treatments]);

  // Transform old API response
  const suspenseTrafficChartData = useMemo(
    () => experimentTrafficToLineChartData(iteration.treatments, experimentTraffic ?? null),
    [iteration.treatments, experimentTraffic],
  );

  // Select data source based on flags and availability. Stay on the dataviz path
  // (loading) while analysis is pending so we do not flash timeseries totals.
  const isDatavizActive = shouldUseDataviz && !isTrafficDatavizError;
  const { chartData, isLoading } = useMemo(() => {
    if (isDatavizActive) {
      return {
        chartData: datavizChartData,
        isLoading: isWaitingForAnalysis || isTrafficDatavizLoading,
      };
    }
    return { chartData: suspenseTrafficChartData, isLoading: trafficFetchEnabled && isFetching };
  }, [
    isDatavizActive,
    isWaitingForAnalysis,
    datavizChartData,
    suspenseTrafficChartData,
    isTrafficDatavizLoading,
    trafficFetchEnabled,
    isFetching,
  ]);

  // Build color map for treatments, using the flag variation index when available
  // so that chart colors match the legend/table (which uses flag variation order).
  const keyToColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    iteration.treatments?.forEach((treatment, index) => {
      const key = treatment.name ?? treatment._id ?? '';
      if (!key) {
        return;
      }
      const variationId = treatment.parameters?.[0]?.variationId;
      const variationIndex =
        variationId && flagVariations ? flagVariations.findIndex((v) => v._id === variationId) : -1;
      map[key] = getVariationFillColor(variationIndex >= 0 ? variationIndex : index);
    });
    return map;
  }, [iteration.treatments, flagVariations]);

  const totalCount = useMemo(() => calculateTrafficTotal(chartData), [chartData]);

  return { chartData, isLoading, isDatavizActive, keyToColorMap, totalCount };
}
